import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppAssistApi } from "./AppAssistApi";
import { AppIcons } from "./AppIcons";
import { AppAssistRules } from "./AppAssistRules";

// AppAssistView - the fixed markup of the AI help beside the chat card: the ✦ button in the composer
// bar, and the panel - heading, log, the "let <name> know?" region, the chips, the question box.
//
// ⭐ ONE AI AREA (owner, 2026-09-26: "There are now two ai areas - combine the bottom one into the right
// one"). The chips and the task-close card used to sit above the composer, under the chat; both live in
// the panel now - the card as the newest entry under the log, the chips just above the Ask box, so they
// are there before the first question and after every answer. The composer bar keeps only the ✦ button.
//
// ⚠ ONLY WHAT NEVER CARRIES THE OWNER'S WORDS IS BUILT AS A STRING HERE. Questions, answers and
// drafts are drawn by `AppAssistPanel` with `textContent`, so nothing the model writes can become
// markup. The contact's name is escaped like every other name on this page.

export class AppAssistView {
    public static readonly RootId: string = "wac-assist";
    public static readonly LogId: string = "wac-alog";
    public static readonly AskFormId: string = "wac-aask";
    public static readonly AskBoxId: string = "wac-aq";
    public static readonly ChipsId: string = "wac-chips";
    public static readonly OpenId: string = "wac-ai-open";
    public static readonly CloseId: string = "wac-ai-close";
    public static readonly ScrimId: string = "wac-ai-scrim";
    /** A chip carries the index of its preset (`AppAssistApi.Presets`), never the question itself. */
    public static readonly AskAttr: string = "data-wac-ask";
    /** A button inside the log: `send`, `edit`, `copy`, `again` or `cancel`, and the entry it belongs to. */
    public static readonly ActAttr: string = "data-wac-act";
    public static readonly IndexAttr: string = "data-wac-i";
    /** The AI mark: a constant, aria-hidden, currentColor SVG (`AppIcons.Spark`) - it replaced the ✦ glyph. */
    public static readonly Glyph: string = AppIcons.Svg(AppIcons.Spark, 16);
    /** The close mark of a panel (was a typed ×): `AppIcons.Close`, inside a labelled `btn ghost icon sm`. */
    public static readonly CloseGlyph: string = AppIcons.Svg(AppIcons.Close, 18);

    /**
     * The ready-made questions, inside the panel just above its Ask box. `List` defaults to
     * `AppAssistApi.Presets`; with tasks on it is `AppTaskRules.Presets` (one more, "What do I owe
     * Anna?"). ⚠ A chip carries its INDEX into that same list - the click handler must read the
     * same list it was drawn from.
     */
    public static Chips = (List: string[] = AppAssistApi.Presets): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<div class=\"chips\" id=\"" + AppAssistView.ChipsId
                + "\" role=\"toolbar\" aria-label=\"Ask AI about this chat\">"];
            for (let At: number = 0; At < List.length; At = At + 1) {
                Out.push("<button type=\"button\" class=\"chip\" " + AppAssistView.AskAttr + "=\"" + String(At)
                    + "\"><span class=\"spark\" aria-hidden=\"true\">" + AppAssistView.Glyph + "</span>"
                    + kHTMLCore.EscapeHtml(List[At]) + "</button>");
            }
            Out.push("</div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistView.Chips");
            return "";
        }
    };

    /** The ✦ button at the left of the composer bar: opens (or closes) the panel. */
    public static OpenButton = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<button type=\"button\" class=\"aibtn btn secondary icon\" id=\"" + AppAssistView.OpenId
                + "\" aria-label=\"Ask AI about this chat\" title=\"Ask AI about this chat\">"
                + AppAssistView.Glyph + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistView.OpenButton");
            return "";
        }
    };

    /**
     * The panel: a column beside the chat on a wide screen, a bottom sheet on a narrow one. `List` is the
     * chips' questions (`Chips`); `Notes` is the task-close region's markup (`AppTaskNotifyView.Region`,
     * "" with tasks off) - passed in, so this view does not import the notifier.
     * ⛔ The log is redrawn whole by `AppAssistPanel.Render`, so the region is its SIBLING, never inside it.
     * `Face` is the contact's disc (`AppAvatarRules.Disc`: their WhatsApp picture, initials or the
     * silhouette) beside "Ask about <name>" - passed in as markup, so this view does not import the
     * avatar loader (it reads through `AppSource`), and `AppMain` lays the picture on with the SAME
     * loader as the list: one blob URL, one download per visit.
     */
    public static Panel = (Title: string, List: string[] = AppAssistApi.Presets, Notes: string = "", Face: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"aiscrim\" id=\"" + AppAssistView.ScrimId + "\"></div>"
                + "<aside class=\"assist\" id=\"" + AppAssistView.RootId + "\" aria-label=\"AI help\">"
                + "<header class=\"ahead\"><span class=\"spark\" aria-hidden=\"true\">" + AppAssistView.Glyph
                + "</span>" + Face + "<h2>" + kHTMLCore.EscapeHtml(AppAssistRules.Heading(Title)) + "</h2>"
                + "<button type=\"button\" class=\"aclose btn ghost icon sm\" id=\"" + AppAssistView.CloseId
                + "\" aria-label=\"Close AI help\" title=\"Close AI help\">" + AppAssistView.CloseGlyph + "</button></header>"
                + "<div class=\"alog\" id=\"" + AppAssistView.LogId + "\" aria-live=\"polite\"></div>"
                // ⛔ No suggested-prompt chips (owner 2026-11-02: "Take away helper texts and suggested prompts").
                + Notes
                + "<form class=\"aask\" id=\"" + AppAssistView.AskFormId + "\">"
                + "<textarea id=\"" + AppAssistView.AskBoxId + "\" rows=\"1\""
                + " aria-label=\"Ask about this chat\" autocomplete=\"off\"></textarea>"
                + "<button type=\"submit\" class=\"ago btn\">Ask</button></form></aside>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistView.Panel");
            return "";
        }
    };
}
